import * as React from 'react';
import { clsx } from 'clsx';
import { Droppable } from 'react-beautiful-dnd';

import { TABLE_COL_WIDTH } from '../../../../constants/table';
import { getCellStyle } from '../../../../utils/table';
import * as styles from './DroppableHeaderRow.css';

export interface IDroppableAreaPosition {
  bottom?: number;
  left?: number;
  right?: number;
  top?: number;
}

export interface IDroppableHeaderRowProps {
  dragging?: boolean;
  droppableAreaPosition?: IDroppableAreaPosition;
  droppableId: string;
  droppableIndex?: number;
  droppableRef?: React.MutableRefObject<HTMLDivElement | null>;
  onDropAreaScroll?(): void;
  position: number;
}

export const DroppableHeaderRowComponent = ({
  dragging,
  children,
  droppableAreaPosition,
  droppableId,
  droppableIndex,
  droppableRef,
  onDropAreaScroll,
  position,
}: React.PropsWithChildren<IDroppableHeaderRowProps>) => {
  const computedStyles = React.useMemo(() => getCellStyle(position), [position]);
  const isFixedArea = !!droppableAreaPosition;
  const style = isFixedArea ? droppableAreaPosition : computedStyles;

  const placeholderStyle = React.useMemo(
    () =>
      droppableIndex !== undefined && droppableIndex >= 0 ? { left: droppableIndex * TABLE_COL_WIDTH } : undefined,
    [droppableIndex],
  );

  return (
    <div
      className={clsx(
        styles['droppable-container'],
        isFixedArea && styles['droppable-container--fixed'],
        dragging && styles['droppable-container--dragging'],
      )}
      ref={droppableRef}
      onScroll={onDropAreaScroll}
      style={style}
    >
      <Droppable droppableId={droppableId} direction="horizontal">
        {(provided, snap) => (
          <div className={styles['droppable-area']} ref={provided.innerRef} {...provided.droppableProps}>
            {children}
            {provided.placeholder}
            {snap.isDraggingOver && placeholderStyle && (
              <div className={styles['droppable-placeholder']} style={placeholderStyle} />
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export const DroppableHeaderRow =
  React.memo<React.PropsWithChildren<IDroppableHeaderRowProps>>(DroppableHeaderRowComponent);
